// AdminTeams.jsx
import { Box, Typography, IconButton, colors, useTheme, useMediaQuery } from "@mui/material";
import { useRef } from "react";
import { styled } from "@mui/material/styles";
import Badge from "@mui/material/Badge";
import Avatar from "@mui/material/Avatar";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import Divider from "@mui/material/Divider";
import AddCallIcon from "@mui/icons-material/AddCall";
import EmailIcon from "@mui/icons-material/Email";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import queen from '../../assets/icons/teams/queen.png'
import bishop from '../../assets/icons/teams/bishop.png'
import custtle from '../../assets/icons/teams/cusstle.png'

const TeamCard = ({ team }) => (
  <Box
    sx={{
      boxShadow: 2,
      p: 1,
      width: "100%",
      height: {xs: 'auto', sm: '420px', md: '420px'},
      borderRadius: "1rem",
      position: "relative",
      minWidth: 0,
    }}
    data-aos="flip-left"
    data-aos-delay="400"
  >
    <Box position={'relative'} overflow={'hidden'} zIndex={1}>
        {team.role === 'supervisor' && (
            <Box
                width={'200px'}
                height={'350px'}
                overflow={'hidden'}
                position={'absolute'}
                top={'-50px'}
                right={'-60px'}
                backgroundColor = {colors.green[100]}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                borderRadius={'50%'}
                zIndex={-1}
            >
                <img src={bishop} alt="bishop" style={{width: 'auto', height: '80%', zIndex: 2, opacity: .5}} />
            </Box>
        )}
        {(team.role === 'admin' || team.role === 'hr') && (
            <Box
                width={'200px'}
                height={'350px'}
                overflow={'hidden'}
                position={'absolute'}
                top={'-50px'}
                right={'-40px'}
                backgroundColor = {colors.green[100]}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                borderRadius={'50%'}
                zIndex={-1}
            >
                <img src={custtle} alt="castle" style={{width: 'auto', height: '75%', zIndex: 2, opacity: .5}} />
            </Box>
        )}
        {team.role === 'manager' && (
            <Box
                width={'200px'}
                height={'350px'}
                overflow={'hidden'}
                position={'absolute'}
                top={'-50px'}
                right={'-60px'}
                backgroundColor = {colors.green[100]}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                borderRadius={'50%'}
                zIndex={-1}
            >
                <img src={queen} alt="queen" style={{width: 'auto', height: '75%', zIndex: 2, opacity: .5}} />
            </Box>
        )}
        
      {team.image ? (
        <Box sx={{ width: "100%", height: { xs: '300px', sm: "250px", md: "280px" }, overflow: "hidden" }}>
          <img
            alt={team.FirstName}
            src={`https://mindsinaction.com.na/assets/media/pictures/${team.image}`}
            style={{
              width: "100%",height: '100%', objectFit: 'cover'
            }}
          />
        </Box>
      ) : (
        <Avatar
          sx={{
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            backgroundColor: colors.blue[100],
          }}
        >
          <SupervisorAccountIcon sx={{ fontSize: "10rem" }} />
        </Avatar>
      )}
    </Box>
    <Typography fontWeight={700} fontSize={"1.2rem"} mt={"1rem"}>
      {team.FirstName} {team.LastName}
    </Typography>
    <Typography variant={'body1'} dangerouslySetInnerHTML={{ __html: team.role_explanation }}/>
    <Box
          sx={{
            position: { xs: "relative", sm: "absolute", md: "absolute" },
            width: { xs: "auto", sm: "95%", md: "95%" },
            bottom: "0",
            mt: { xs: "1rem", sm: "0", md: "0" },
          }}
        >
          <Divider sx={{ border: ".1rem solid black" }} />
          <Box display={"flex"} alignItems={'center'} justifyContent={"start"} p={1}>
            <Box>
              <IconButton href={`mailto:${team.email}`}>
                <EmailIcon /> 
              </IconButton>
            </Box>
            <Typography variant="body2" marginLeft={'1rem'}>{team.email}</Typography>
          </Box>
        </Box>
  </Box>
);

const AdminTeams = ({ teams = [] }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef(null);
  
  const itemWidth = isXs ? 300 : isSm ? 280 : 300;
  const totalSlides = teams.length;

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -itemWidth,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: itemWidth,
        behavior: 'smooth'
      });
    }
  };

  if (totalSlides === 0) return null;

  return (
    <Box sx={{ 
      position: "relative", 
      display: "flex", 
      alignItems: "center",
      width: "100%",
      padding: '0 1rem'
    }}>
      
      {/* Left Scroll Button */}
      <IconButton
        onClick={scrollLeft}
        sx={{
          position: "absolute",
          left: 0,
          zIndex: 10,
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 1)",
          },
          display: { xs: "none", sm: "flex" },
          boxShadow: 2,
        }}
      >
        <ChevronLeftIcon />
      </IconButton>

      {/* Teams Container */}
      <Box 
        ref={scrollContainerRef}
        sx={{ 
          display: "flex", 
          height: "auto", 
          width: "100%", 
          overflowX: "auto", 
          gap: 1, 
          justifyContent: "start", 
          padding: '.5rem',
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {/* Teams cards */}
        {teams.map((team) => (
          <Box
            key={team.Id}
            sx={{
              flex: "0 0 auto",
              width: `${itemWidth}px`,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TeamCard team={team} />
          </Box>
        ))}
      </Box>

      {/* Right Scroll Button */}
      <IconButton
        onClick={scrollRight}
        sx={{
          position: "absolute",
          right: 0,
          zIndex: 10,
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 1)",
          },
          display: { xs: "none", sm: "flex" },
          boxShadow: 2,
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

export default AdminTeams;